import { ENTRY_TYPE } from 'src/pages/patrimony/PatrimonyPortfolio/tabs/PatrimonyPortfolioEstimation/PatrimonyPortfolioEstimation.const'
import { $t } from 'src/plugins/i18n'
import type { BudgetRecordTypes } from 'src/services/budget/budgetApiService.const'
import { RECORD_TYPES } from 'src/services/budget/budgetApiService.const'
import { keepRowFromEnumKeys } from 'src/services/mappings/mapping.utils'
import {
  computePersonFullName,
  getPartner,
  getRelatedPerson,
} from 'src/services/mappings/relation/relation.utils'
import type {
  ContextualFieldInterface,
  FieldInterface,
} from 'src/tpbp-configuration/mapper/mappingInterfaces'
import { OWNERSHIP_CODES } from 'src/tpbp-configuration/models/models.const'
import { FEATURE_IDS } from 'src/tpbp-core/feature-flag/featureFlags.const'
import { hasFeature } from 'src/tpbp-core/feature-flag/featureFlags.utils'
import { EXPENSE_CATEGORY } from 'src/tpbp-core/models/budget/budgetItem.const'
import type { EnumInterface, Scalar } from 'src/tpbp-core/models/global.type'
import { budgetApiServiceStore } from 'src/tpbp-json-api-handler/budget/budgetApiServiceStore'
import type {
  BudgetItemModel,
  PersonModel,
} from 'src/tpbp-json-api-handler/models/models'

export function getEntryCategoriesForPortfolio(portfolio) {
  const { productKind } = portfolio

  const categories =
    budgetApiServiceStore.getEntryCategoriesForProductKind(productKind)

  return categories || []
}

export function getEntryKindsForPortfolio(portfolio) {
  const { productKind } = portfolio

  const categories =
    budgetApiServiceStore.getEntryKindsForProductKind(productKind)

  return categories || []
}

export function getLinkedPortfolioEnums(
  record: Record<string, unknown>,
): EnumInterface[] {
  const { _linkablePortfolios } = record as any

  return (_linkablePortfolios ?? []).map(({ name: label, id: key }) => ({
    key,
    label,
  }))
}

export function getOwnerNewDynamicEnumLabel(record: BudgetItemModel): string {
  const { ownershipCode, _titulars } = record

  if (ownershipCode === OWNERSHIP_CODES.common) {
    return $t('budget.scope.Common')
  }
  const titular = _titulars?.[0]

  return titular ? `${titular.firstName} ${titular.lastName}` : ''
}

export function getOwner(record: BudgetItemModel): string {
  const { ownershipCode, _titulars } = record

  if (ownershipCode === OWNERSHIP_CODES.common) {
    return $t('budget.scope.Common')
  }

  const titular = _titulars?.[0]

  return titular ? `${titular.firstName} ${titular.lastName}` : ''
}

export function getOwnerNewEnums(
  record: Record<string, unknown>,
): EnumInterface[] {
  const { _titulars } = record as any

  if (!_titulars?.length) {
    return []
  }

  const titular = _titulars?.[0]
  const owners = [
    {
      key: titular.id,
      label: `${titular.firstName} ${titular.lastName}`,
    },
  ]

  const partner = getPartner(titular)
  if (partner) {
    owners.push({
      key: partner.id,
      label: `${partner.firstName} ${partner.lastName}`,
    })
    owners.unshift({
      key: OWNERSHIP_CODES.common,
      label: $t('budget.scope.Common'),
    })
  }

  return owners
}

export function getRelationEnums(record, currentUser: PersonModel) {
  if (!currentUser) {
    return []
  }
  const relations = currentUser._relations ?? []

  const list = [
    {
      key: currentUser.id,
      label: `${currentUser.firstName} ${currentUser.lastName}`,
    },
  ]

  const listOther = relations.map((relation) => {
    const related = getRelatedPerson(relation, currentUser.id)

    return {
      key: related.id,
      label: `${related.firstName} ${related.lastName}`,
    }
  })

  list.push(...listOther)

  return list.filter((person) => person.key !== record.id)
}

export function setDefaultOnSingleEnums(
  fields: ContextualFieldInterface[],
  record: Record<string, unknown>,
) {
  for (const field of fields) {
    setDefaultOnSingleEnumsField(field, record)
  }
}

function setDefaultOnSingleEnumsField(
  field: ContextualFieldInterface,
  record: Record<string, unknown>,
) {
  const { enums, key } = field
  if (enums?.length !== 1) return
  const value = enums[0]?.key
  if (record[key] !== value) {
    record[key] = value
  }
}

export function budgetFieldIsMandatory(record, key) {
  const { entryKind } = record

  const entryKindConfiguration = entryKind
    ? budgetApiServiceStore.getEntryKindConfiguration(entryKind)
    : undefined

  return entryKindConfiguration?.requiredAttributes?.includes(key) || false
}

export function entryKindHasOptional(
  entryKind: Scalar | undefined,
  key: string,
  apiServiceStore = budgetApiServiceStore,
) {
  const entryKindConfiguration = entryKind
    ? apiServiceStore.getEntryKindConfiguration(entryKind)
    : undefined

  return entryKindConfiguration?.optionalAttributes?.includes(key) || false
}

export function budgetFieldIsOptional(record, key) {
  const { entryKind } = record

  return entryKindHasOptional(entryKind, key)
}

function filterEntryKinds(entryKinds, record) {
  const { _linkedPortfolios, $isNew } = record

  if ($isNew && _linkedPortfolios && _linkedPortfolios.length > 0) {
    const entryKindsForPortfolio = getEntryKindsForPortfolio(
      _linkedPortfolios[0],
    )

    const filterEntryKinds = entryKinds.filter(({ key }) =>
      entryKindsForPortfolio.includes(key),
    )

    return filterEntryKinds
  }

  return entryKinds
}

function sortEntryKinds(entryKinds) {
  const entryKindsWithOrderAndHide = entryKinds
    .filter((a) => !a.hidden)
    .sort((a, b) => {
      return a.entryKindOrder - b.entryKindOrder
    })

  return entryKindsWithOrderAndHide
}

export function getBudgetItemEntryKindEnums(record) {
  const entryKinds = budgetApiServiceStore.getCategoriesEntryKinds(
    record.entryCategory,
    record.ownershipCode,
  )
  const filteredEntryKinds = filterEntryKinds(entryKinds, record)
  const sortedEntryKinds = sortEntryKinds(filteredEntryKinds)
  const hasFeatureEvolMvp3 = hasFeature(FEATURE_IDS.evolMvp3)
  return hasFeatureEvolMvp3 ? sortedEntryKinds : filteredEntryKinds
}

function filterCategories(categories, record) {
  const { _linkedPortfolios, $isNew } = record

  if ($isNew && _linkedPortfolios && _linkedPortfolios.length > 0) {
    const entryCategoriesForPortfolio = getEntryCategoriesForPortfolio(
      _linkedPortfolios[0],
    )

    const filterCategories = categories.filter(({ key }) =>
      entryCategoriesForPortfolio.includes(key),
    )

    return filterCategories
  }

  return categories
}

/**
 * Converts RECORD_TYPES to ENTRY_TYPE
 * @param entryType RECORD_TYPES
 * @return ENTRY_TYPE
 */
export function budgetRecordTypeToEntryType(
  entryType: BudgetRecordTypes,
): BudgetRecordTypes {
  return entryType === RECORD_TYPES.expense ? ENTRY_TYPE.liability : entryType
}

function getBudgetItemCategoryEnums(record: BudgetItemModel): Scalar[] {
  const { entryType } = record
  const categoriesByType = budgetApiServiceStore.getCategoriesByType()
  const categories = categoriesByType[entryType]
  const filteredCategories = filterCategories(categories, record)
  return filteredCategories.map(({ key }) => key)
}

export function getBudgetItemEnums(
  record: BudgetItemModel,
  field: FieldInterface,
) {
  const { key } = field

  if (key === 'entryCategory') {
    const enums = getBudgetItemCategoryEnums(record)
    // fix for TF-3442, does not let users create expense category, this info should come from backend
    return enums.filter(
      (enumKey) =>
        !record.$isNew || (record.$isNew && enumKey !== EXPENSE_CATEGORY),
    )
  }
  if (key === 'displayedCategory') {
    return getBudgetItemCategoryEnums(record)
  }
  if (key === 'entryKind') {
    throw new Error('Should not be called')
  }

  const enumKeys = getBudgetItemEnumsFromField(record, field)

  return enumKeys && key === 'frequency'
    ? getBudgetItemFrequencyEnums(record, enumKeys)
    : (enumKeys as Scalar[])
}

export function getBudgetItemFrequencyEnums(
  record: BudgetItemModel,
  enumKeys: Scalar[],
) {
  const keepOnlySingle = ['at_date', 'acquisition_date'].includes(
    record.fromType,
  )
  if (keepOnlySingle) return keepRowFromEnumKeys(enumKeys, 'single')
  return enumKeys
}

export function getBudgetItemEnumsFromField(
  record: BudgetItemModel,
  field: FieldInterface,
) {
  const { entryKind } = record
  const entryKindConfiguration = entryKind
    ? budgetApiServiceStore.getEntryKindConfiguration(entryKind)
    : undefined

  if (
    entryKindConfiguration &&
    Object.hasOwn(entryKindConfiguration.enums, field.originKey)
  ) {
    return entryKindConfiguration.enums[field.originKey]
  }

  return field.enumKeys
}

export function canShowBudgetRepartition(budgetItem: BudgetItemModel) {
  if (!hasFeature(FEATURE_IDS.coupleApproach)) return false
  if (budgetItem._titulars?.length < 2) return false
  return budgetItem.ownershipCode === OWNERSHIP_CODES.common
}

export function getBudgetTitularsNames(budgetItem: BudgetItemModel) {
  return budgetItem._titulars.map((person) => computePersonFullName(person))
}

function filterEntryKindFieldEnumHasOptional(
  entryKindField: ContextualFieldInterface,
  optionalKey: string,
  apiServiceStore = budgetApiServiceStore,
) {
  const enums = entryKindField.enums?.filter(({ key }) =>
    entryKindHasOptional(key, optionalKey, apiServiceStore),
  )
  return { ...entryKindField, enums }
}

export function computeFieldsEnums(
  fields: ContextualFieldInterface[],
  isTaxation: boolean,
  apiServiceStore = budgetApiServiceStore,
) {
  return fields.map((field) => {
    if (isTaxation && field.key === 'entryKind') {
      return filterEntryKindFieldEnumHasOptional(
        field,
        'yearly_taxable_income',
        apiServiceStore,
      )
    }
    return field
  })
}

export function filteredTaxSystemWhenShouldNotDisplayed(
  fields: ContextualFieldInterface[],
  isTaxation: boolean,
): ContextualFieldInterface[] {
  return fields.filter((field) => isTaxation || field.key !== 'taxSystem')
}
